import { PolarisIcon } from '@/exports/columnCommonComponents';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

type ManualPastingProps = {
  values: string[];
  onChange: (values: string[]) => void;
};

const ManualPasting = ({ values, onChange }: ManualPastingProps) => {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const textareaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue('');
  }, [values]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const onRemoveValue = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    onChange(newValues);
  };

  const parseAndAddValues = (input: string) => {
    const newIds = input
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0 && !values.includes(id));

    if (newIds.length > 0) {
      onChange([...values, ...newIds]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        parseAndAddValues(inputValue);
      }
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      parseAndAddValues(inputValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const currentInput = inputValue + pastedText;
    setInputValue(currentInput);

    // Auto-parse if it contains commas
    if (pastedText.includes(',')) {
      setTimeout(() => {
        parseAndAddValues(currentInput);
      }, 0);
    }
  };

  const handleInputAreaClick = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleContainerClick = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleChipTextClick = (index: number, value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditValue(value);
  };

  const handleChipTextBlur = () => {
    if (editingIndex !== null && editValue.trim() !== '' && editValue !== values[editingIndex]) {
      const newValues = [...values];
      newValues[editingIndex] = editValue.trim();
      onChange(newValues);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleChipTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  const handleChipTextKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChipTextBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingIndex(null);
      setEditValue('');
    }
  };

  return (
    <Container onClick={handleContainerClick}>
      <InputArea onClick={handleInputAreaClick}>
        <ChipsContainer>
          {values.map((value, index) => (
            <ChipContainer key={`${value}-${index}`}>
              {editingIndex === index ? (
                <ChipInput
                  value={editValue}
                  onChange={handleChipTextChange}
                  onBlur={handleChipTextBlur}
                  onKeyDown={handleChipTextKeyDown}
                  autoFocus
                />
              ) : (
                <ChipText onClick={(e) => handleChipTextClick(index, value, e)}>{value}</ChipText>
              )}
              <ChipIconButton onClick={() => onRemoveValue(index)}>
                <PolarisIcon height={14} icon="close" />
              </ChipIconButton>
            </ChipContainer>
          ))}
        </ChipsContainer>
        <StyledInput
          onBlur={handleBlur}
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={values.length === 0 ? 'Paste IDs here' : ''}
        />
      </InputArea>
    </Container>
  );
};

const Container = styled.div`
  min-height: 539px;
  padding: ${(props) => props.theme.spacing(3)};
  background-color: ${(props) => props.theme.palette.background.default};
  border-radius: 8px;
  border: 1px dashed ${(props) => props.theme.palette.primary.main};
  padding-top: ${(props) => props.theme.spacing(11)};
`;

const InputArea = styled.div`
  min-height: 120px;
  padding: ${(props) => props.theme.spacing(1.5)};
  cursor: text;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1)};
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(0.5)};
  min-height: 0;
  max-height: 500px;
  overflow: auto;
`;

const ChipContainer = styled.div`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--Semantic-Info-Light, #3999c8);
  border-radius: 16px;
  padding: ${(props) => props.theme.spacing(0.25)} ${(props) => props.theme.spacing(0.5)};
  height: 38px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ChipText = styled.span`
  color: ${(props) => props.theme.palette.text.primary};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: ${(props) => props.theme.spacing(0.5)};
  cursor: pointer;
  flex: 1;
`;

const ChipInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: ${(props) => props.theme.palette.text.primary};
  font-family: inherit;
  font-size: 12px;
  margin-right: ${(props) => props.theme.spacing(0.5)};
  flex: 1;
  width: 100%;
`;

const ChipIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.palette.text.secondary};
  min-width: 16px;
  height: 16px;
  border-radius: 50%;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${(props) => props.theme.palette.text.primary};
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: ${(props) => props.theme.palette.text.primary};
  font-family: inherit;
  font-size: 14px;
  flex: 1;
  min-width: 200px;

  &::placeholder {
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

export default ManualPasting;
